
import { Link } from 'react-router-dom';
import { PageMeta } from '../../components/shared/PageMeta';
import { LegalPageLayout, LegalSection } from '../../components/feature/LegalPageLayout';
import { LEGAL_LAST_UPDATED } from '../../content/legalDates';

export default function PrivacyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated={LEGAL_LAST_UPDATED.privacy}>
      <PageMeta path="/privacy" />
      <LegalSection title="Who we are">
        <p>
          This policy explains how Driscop Ltd ("Driscop", "we", "us", "our"), a company
          registered in England and Wales and based in Manchester, UK, collects and uses
          personal data when you visit driscop.co.uk or submit a form on this site.
        </p>
        <p>
          For anything in this policy, or to exercise any of the rights described below,
          contact us at{' '}
          <a href="mailto:hello@driscop.co.uk" className="text-blue-400 hover:text-blue-300">
            hello@driscop.co.uk
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="What information we collect">
        <p>
          We only collect personal data that you choose to give us directly, by filling in
          a form on this site. Depending on the form, this may include your name, email
          address, phone number, job role, company details, and anything you write in a
          message or questionnaire field.
        </p>
        <p>
          We do not collect personal data through cookies, analytics, or any other
          automated tracking technology. See our{' '}
          <Link to="/cookies" className="text-blue-400 hover:text-blue-300">
            Cookie Policy
          </Link>{' '}
          for more on this.
        </p>
      </LegalSection>

      <LegalSection title="How we use your information">
        <p>We use the information you submit to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Respond to you and provide whatever you asked for through that form;</li>
          <li>Understand demand for our products so we can plan our roadmap.</li>
        </ul>
        <p>
          Where a form asks for your consent, we rely on that{' '}
          <span className="text-gray-300">consent</span> as our legal basis. Otherwise, we
          rely on our <span className="text-gray-300">legitimate interest</span> in
          responding to enquiries you have sent us. You can withdraw consent at any time by
          emailing us. This won't affect anything we did before you withdrew it.
        </p>
        <p>
          We do not sell your data, use it for automated decision-making, or share it with
          third parties for their own marketing purposes.
        </p>
      </LegalSection>

      <LegalSection title="Who we share it with">
        <p>
          We do not share your personal data with third parties, except where necessary to
          operate this website (for example, our hosting and infrastructure providers, who
          process data on our behalf and only under our instructions), or where we're
          required to by law.
        </p>
      </LegalSection>

      <LegalSection title="How long we keep it">
        <p>
          We keep form submissions for as long as needed to fulfil the purpose you gave
          them to us for, and no longer than that, unless you ask us to delete it sooner or
          the law requires us to keep it for longer.
        </p>
      </LegalSection>

      <LegalSection title="Your rights">
        <p>Under UK GDPR, you have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Ask for a copy of the personal data we hold about you;</li>
          <li>Ask us to correct inaccurate or incomplete data;</li>
          <li>Ask us to delete your data or stop processing it;</li>
          <li>Withdraw consent at any time, where we rely on consent;</li>
          <li>Ask us to restrict how we use your data, or to receive it in a portable format;</li>
          <li>Object to how we use your data.</li>
        </ul>
        <p>
          To exercise any of these rights, email{' '}
          <a href="mailto:hello@driscop.co.uk" className="text-blue-400 hover:text-blue-300">
            hello@driscop.co.uk
          </a>
          . You also have the right to complain to the UK Information Commissioner's Office
          (ICO) at{' '}
          <a
            href="https://ico.org.uk/make-a-complaint/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300"
          >
            ico.org.uk
          </a>
          , though we'd appreciate the chance to address your concern directly first.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          This site and its forms are intended for business use and are not directed at
          children. We do not knowingly collect personal data from children.
        </p>
      </LegalSection>

      <LegalSection title="Security">
        <p>
          We take reasonable technical and organisational measures to protect the
          information you give us against unauthorised access, loss, or misuse.
        </p>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <p>
          We may update this policy as the site and our products evolve. Material changes
          will be reflected by updating the "Last updated" date at the top of this page.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
